import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, animate, AnimatePresence } from 'framer-motion';
import styles from '../style/PortfolioPage.module.css';
import SEO from '../components/SEO';
import { useGetPortfoliosQuery } from '../redux/api';
import ParticleBackground from "../components/ParticleBackground";

// Import icons
import {
    FaPalette, FaPaintBrush,
    FaChartLine, FaCloud
} from 'react-icons/fa';

// Category mapping for icons (colors are now handled in CSS)
const categoryConfigs = {
    marketing: { icon: <FaChartLine /> },
    branding: { icon: <FaPalette /> },
    design: { icon: <FaPaintBrush /> },
    tech: { icon: <FaCloud /> }
};

// Filter options
const filters = [
    { id: 'all', label: 'All Services' },
    { id: 'marketing', label: 'Marketing' },
    { id: 'branding', label: 'Branding' },
    { id: 'design', label: 'Design' },
    { id: 'tech', label: 'Technology' }
];

// Stats data with actual values for counting animation
const statsData = [
    { id: 'clients', number: 80, label: "Happy Clients", duration: 2 },
    { id: 'projects', number: 95, label: "Projects Delivered", duration: 2.5 },
    { id: 'team', number: 18, label: "Team Members", duration: 1.5 },
    { id: 'awards', number: 12, label: "Awards Won", duration: 3 }
];

const Portfolio = () => {
    const { data: apiPortfolios, isLoading } = useGetPortfoliosQuery();
    const [activeFilter, setActiveFilter] = useState('all');
    const [statsCounters, setStatsCounters] = useState({
        clients: 0,
        projects: 0,
        team: 0,
        awards: 0
    });

    // Use scroll for parallax effects
    const { scrollY } = useScroll();

    // Transform scrollY for parallax effects
    const heroOpacity = useTransform(scrollY, [0, 300], [1, 0.8]);
    const heroScale = useTransform(scrollY, [0, 300], [1, 1.05]);
    const scrollProgress = useTransform(scrollY, [0, 5000], [0, 1]);

    // Map API data to Portfolio items
    const portfolioItems = apiPortfolios?.map((item, index) => {
        const config = categoryConfigs[item.category] || categoryConfigs.marketing;
        return {
            id: item._id,
            number: (index + 1).toString().padStart(3, '0'),
            title: item.title,
            category: item.category || 'marketing',
            description: item.description,
            icon: config.icon,
            link: item.link,
            image: item.image
        };
    }) || [];

    // Filtered items based on active filter
    const filteredItems = activeFilter === 'all'
        ? portfolioItems
        : portfolioItems.filter(item => item.category === activeFilter);

    // Animation for stats counting
    useEffect(() => {
        const controls_list = statsData.map(stat => {
            return animate(0, stat.number, {
                duration: stat.duration,
                ease: "easeOut",
                onUpdate(value) {
                    setStatsCounters(prev => ({
                        ...prev,
                        [stat.id]: Math.floor(value)
                    }));
                }
            });
        });

        return () => controls_list.forEach(controls => controls.stop());
    }, []);

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    const cardVariants = {
        hidden: { scale: 0.9, opacity: 0 },
        visible: {
            scale: 1,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 100,
                damping: 15
            }
        },
        hover: {
            scale: 1.02,
            y: -10,
            transition: {
                duration: 0.2
            }
        }
    };

    const filterVariants = {
        hidden: { opacity: 0, x: -20 },
        visible: {
            opacity: 1,
            x: 0,
            transition: {
                duration: 0.3
            }
        }
    };

    const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.createchsquads.com";

    // Add scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <motion.div
            className={styles.portfolioPage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
        >
            <SEO
                title="Digital Marketing & IT Portfolio"
                description="Explore CreaTech Squads' portfolio of 95+ delivered projects across social media marketing, branding, UI/UX design, digital advertising, cloud services and more."
                keywords="CreaTech Squads portfolio, digital marketing work, branding projects India, UI UX design portfolio, web development projects"
                canonical="/portfolio"
            />
            
            {/* Hero Section with Particle Background */}
            <motion.section
                className={styles.heroSection}
                style={{ opacity: heroOpacity, scale: heroScale }}
            >
                <ParticleBackground />
                <div className={styles.container}>
                    <motion.div
                        className={styles.heroContent}
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        <motion.h1
                            className={styles.heroTitle}
                            initial={{ x: -50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                        >
                            Our <motion.span
                                className={styles.highlight}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.4, type: "spring" }}
                            >
                                Portfolio
                            </motion.span>
                        </motion.h1>
                        <motion.p
                            className={styles.heroDescription}
                            initial={{ y: 30, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4, duration: 0.6 }}
                        >
                            Discover our comprehensive range of digital solutions that have helped businesses
                            transform their online presence and achieve remarkable growth.
                        </motion.p>
                    </motion.div>
                </div>
            </motion.section>

            {/* Stats Section */}
            <motion.section
                className={styles.statsSection}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8 }}
            >
                <div className={styles.container}>
                    <motion.div
                        className={styles.statsGrid}
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                    >
                        {statsData.map((stat, index) => (
                            <motion.div
                                key={stat.id}
                                className={styles.statItem}
                                variants={itemVariants}
                                whileHover={{ scale: 1.05, borderColor: "rgba(102, 255, 51, 0.4)" }}
                            >
                                <motion.div
                                    className={styles.statNumber}
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.1 * index, type: "spring" }}
                                >
                                    {statsCounters[stat.id]}
                                    <span className={styles.statPlus}>+</span>
                                </motion.div>
                                <div className={styles.statLabel}>{stat.label}</div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </motion.section>

            {/* Filter Section */}
            <motion.section
                className={styles.filterSection}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5 }}
            >
                <div className={styles.container}>
                    <div className={styles.filterContainer}>
                        <motion.div
                            className={styles.filterTitle}
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.2 }}
                        >
                            Filter by Category:
                        </motion.div>
                        <motion.div
                            className={styles.filterButtons}
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            {filters.map((filter, index) => (
                                <motion.button
                                    key={filter.id}
                                    className={`${styles.filterBtn} ${activeFilter === filter.id ? styles.active : ''}`}
                                    onClick={() => setActiveFilter(filter.id)}
                                    variants={filterVariants}
                                    custom={index}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    {filter.label}
                                </motion.button>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            {/* Portfolio Grid Section */}
            <motion.section
                className={styles.portfolioSection}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.5 }}
            >
                <div className={styles.container}>
                    {isLoading ? (
                        <div className={styles.loadingWrapper}>
                            <motion.div 
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                className={styles.loader}
                            />
                        </div>
                    ) : (
                        <motion.div
                            className={styles.portfolioGrid}
                            layout
                            transition={{ layout: { duration: 0.4, type: "spring", stiffness: 100 } }}
                        >
                            <AnimatePresence mode='popLayout'>
                                {filteredItems.map((item, index) => (
                                    <motion.div
                                        key={item.id}
                                        className={styles.portfolioCard}
                                        variants={cardVariants}
                                        initial="hidden"
                                        animate="visible"
                                        exit={{ scale: 0.8, opacity: 0 }}
                                        whileHover="hover"
                                        viewport={{ once: true, amount: 0.3 }}
                                        layout
                                    >
                                        <div className={styles.cardHeader}>
                                            <motion.div
                                                className={styles.cardNumber}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.2 }}
                                            >
                                                {item.number}
                                            </motion.div>
                                            <motion.div
                                                className={styles.cardIcon}
                                                whileHover={{ rotate: 10, scale: 1.1 }}
                                                transition={{ type: "spring", stiffness: 200 }}
                                            >
                                                {item.icon}
                                            </motion.div>
                                        </div>

                                        <div className={styles.cardImageWrapper}>
                                            <img 
                                                src={`${API_BASE_URL}/${item.image.replace(/\\/g, "/")}`} 
                                                alt={item.title} 
                                                className={styles.cardImage}
                                            />
                                        </div>

                                        <div className={styles.cardContent}>
                                            <motion.h3
                                                className={styles.cardTitle}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.3 }}
                                            >
                                                {item.title}
                                            </motion.h3>
                                            <motion.p
                                                className={styles.cardDescription}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ delay: 0.4 }}
                                            >
                                                {item.description}
                                            </motion.p>
                                        </div>

                                        <div className={styles.cardFooter}>
                                            <motion.span
                                                className={styles.cardCategory}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ delay: 0.5 }}
                                                whileHover={{ scale: 1.05 }}
                                            >
                                                {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                                            </motion.span>
                                            {item.link && (
                                                <motion.a
                                                    href={item.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.cardAction}
                                                    whileHover={{ x: 5 }}
                                                    whileTap={{ scale: 0.95 }}
                                                >
                                                    View Details →
                                                </motion.a>
                                            )}
                                        </div>

                                        <motion.div
                                            className={styles.cardHoverEffect}
                                            initial={{ opacity: 0 }}
                                            whileHover={{ opacity: 1 }}
                                        />
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </motion.div>
                    )}
                </div>
            </motion.section>

            {/* CTA Section */}
            <motion.section
                className={styles.ctaSection}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8 }}
            >
                <div className={styles.container}>
                    <motion.div
                        className={styles.ctaContent}
                        initial={{ y: 50, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <motion.h2
                            className={styles.ctaTitle}
                            initial={{ scale: 0.9, opacity: 0 }}
                            whileInView={{ scale: 1, opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2, type: "spring" }}
                        >
                            Ready to Transform Your Digital Presence?
                        </motion.h2>
                        <motion.p
                            className={styles.ctaDescription}
                            initial={{ y: 20, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3 }}
                        >
                            Let's collaborate to create exceptional digital experiences that drive results.
                        </motion.p>
                        <motion.div
                            className={styles.ctaButtons}
                            variants={containerVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <motion.button
                                className={styles.ctaBtnPrimary}
                                variants={itemVariants}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => window.location.href = '/contact'}
                            >
                                Start a Project
                            </motion.button>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>

            <motion.div
                className={styles.scrollProgress}
                style={{ scaleX: scrollProgress }}
            />
        </motion.div>
    );
};

export default Portfolio;
